import React from 'react';
import { motion } from 'framer-motion';
import FadeIn from './shared/FadeIn';
import { SOCIAL_LINKS, RESUME_URL } from '../data/constants';

const LINKS = [
  {
    label: SOCIAL_LINKS.email,
    href: `mailto:${SOCIAL_LINKS.email}`,
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    ),
    fill: false,
  },
  {
    label: 'github.com/pro1code1hack',
    href: SOCIAL_LINKS.github,
    icon: (
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
    ),
    fill: true,
    external: true,
  },
  {
    label: 'LinkedIn',
    href: SOCIAL_LINKS.linkedin,
    icon: (
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    ),
    fill: true,
    external: true,
  },
  {
    label: 'Bristol, United Kingdom',
    icon: (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </>
    ),
    fill: false,
  },
];

const Contact = () => (
  <FadeIn id="contact" className="py-28 content-auto">
    {/* Decorative gradient line */}
    <div
      className="max-w-6xl mx-auto px-6 mb-16"
      aria-hidden="true"
    >
      <div className="h-px w-full gradient-line" />
    </div>

    <div className="max-w-2xl mx-auto px-6 text-center">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <span className="text-xs font-mono text-green/50 tracking-widest mb-3 block" aria-hidden="true">05</span>
        <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Get In Touch</h2>
        <p className="text-txt-secondary mb-12 text-lg leading-relaxed">
          Open to opportunities, collaborations, and conversations about engineering, startups, and technology.
        </p>
      </motion.div>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12 flex-wrap">
        {LINKS.map((link, i) => {
          const inner = (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.1 }}
              className={`flex items-center gap-2.5 text-txt-secondary ${link.href ? 'hover:text-green' : ''} transition-colors duration-200 group`}
            >
              <svg
                className="w-4 h-4 text-green/60 group-hover:text-green transition-colors"
                fill={link.fill ? 'currentColor' : 'none'}
                stroke={link.fill ? 'none' : 'currentColor'}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                {link.icon}
              </svg>
              <span className="text-sm">{link.label}</span>
            </motion.span>
          );

          return link.href ? (
            <a
              key={i}
              href={link.href}
              target={link.external ? '_blank' : undefined}
              rel={link.external ? 'noopener noreferrer' : undefined}
              className="py-3 px-3"
            >
              {inner}
              {link.external && <span className="sr-only"> (opens in new tab)</span>}
            </a>
          ) : (
            <div key={i}>{inner}</div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 0.3 }}
        className="flex flex-wrap items-center justify-center gap-4"
      >
        <a
          href={`mailto:${SOCIAL_LINKS.email}`}
          className="inline-block px-8 py-3.5 bg-green text-surface text-sm font-semibold rounded-lg btn-glow hover:bg-green/90 transition-all duration-300"
        >
          Send Email
        </a>
        <a
          href={RESUME_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block px-8 py-3.5 border border-border text-txt text-sm font-medium rounded-lg hover:border-green/40 hover:text-green transition-all duration-300"
        >
          Download Resume
          <span className="sr-only"> (opens in new tab)</span>
        </a>
      </motion.div>
    </div>
  </FadeIn>
);

export default Contact;
